/**
 * NativeBootstrap — montato in <App>. Si avvia all'apertura dell'app
 * e quando l'utente è loggato registra il device per le push APNs,
 * configura keyboard handling, status bar, splash screen e gestisce
 * il tap sulle notifiche per navigare alla schermata giusta.
 *
 * iter v29 r168 — App Store Settimana 2.
 * Su browser/Android non fa nulla (early return).
 */
/* eslint-disable no-empty --
   I catch silenziosi sui plugin Capacitor opzionali sono intenzionali:
   se il plugin non è installato sulla build (es. SplashScreen mancante)
   l'app deve continuare senza esplodere. */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isNative, isIOS, installGlobalHaptic, nativeOnDeepLink } from "@/lib/native";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

export default function NativeBootstrap() {
  const { user } = useAuth();
  const nav = useNavigate();

  // ─── One-time native setup (status bar, splash, keyboard) ──────────
  useEffect(() => {
    if (!isNative) return;
    // iter v29 r220 — Global haptic listener: ogni click su CTA dotato di
    // [data-haptic] o classe btn-primary/btn-cta/btn-action fa scattare il
    // Taptic Engine. Tipica "app feel" iOS.
    installGlobalHaptic();
    (async () => {
      try {
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        try { await StatusBar.setStyle({ style: Style.Light }); } catch (_) {}
        if (isIOS) {
          try { await StatusBar.setBackgroundColor({ color: "#1C1C1A" }); } catch (_) {}
        }
        try {
          const { SplashScreen } = await import("@capacitor/splash-screen");
          await SplashScreen.hide();
        } catch (_) {}
        // iter v29 r168 — Keyboard handling iOS: quando la tastiera si
        // apre, iOS deve spingere SU il contenuto invece di sovrapporlo.
        try {
          const { Keyboard, KeyboardResize } = await import("@capacitor/keyboard");
          await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
          await Keyboard.setScroll({ isDisabled: false });
        } catch (_) { /* plugin non installato su web */ }
      } catch (_) { /* ignore */ }
    })();
  }, []);

  // ─── Universal Links → in-app navigation ──────────────────────────
  // iter v30 r140 — Quando l'utente tocca un link prontoimmo.it sul telefono
  // (es. "Apri Pronto" da email magic-link), iOS apre direttamente l'app
  // grazie ad apple-app-site-association. Capacitor ci consegna l'URL via
  // appUrlOpen: estraiamo il path + query e navighiamo in-app, così non
  // facciamo un full reload del WebView (più veloce e mantiene lo stato).
  useEffect(() => {
    if (!isNative) return undefined;
    let unsubscribe = () => {};
    (async () => {
      unsubscribe = await nativeOnDeepLink((url) => {
        try {
          const parsed = new URL(url);
          // Solo i nostri domini: prontoimmo.it (prod) + preview env. Tutto
          // il resto lo ignoriamo (Apple verifica già il dominio via AASA).
          const path = parsed.pathname + parsed.search + parsed.hash;
          if (!path || path === "/") return;
          // Hash session_id (Emergent Google OAuth) → lascia gestire ad AuthCallback
          // via meccanismo già esistente in AuthProvider; in tutti gli altri
          // casi navighiamo direttamente alla rotta interna.
          if (parsed.hash && parsed.hash.includes("session_id=")) {
            nav(path);
            return;
          }
          nav(path);
        } catch (_) {
          /* URL malformato: ignora */
        }
      });
    })();
    return () => {
      try { unsubscribe(); } catch (_) { /* no-op */ }
    };
  }, [nav]);

  // ─── Push registration + handlers (richiede user loggato) ──────────
  useEffect(() => {
    if (!isNative || !user?.user_id) return;
    const handles = [];
    (async () => {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        const perm = await PushNotifications.checkPermissions();
        let granted = perm.receive === "granted";
        if (!granted) {
          const req = await PushNotifications.requestPermissions();
          granted = req.receive === "granted";
        }
        if (!granted) return;

        // 1. Token APNs ricevuto → mandalo al backend
        handles.push(await PushNotifications.addListener(
          "registration",
          async (token) => {
            const apnsToken = token?.value;
            if (!apnsToken) return;
            try {
              await api.post("/devices/push-token", {
                token: apnsToken,
                platform: "ios",
                bundle_id: "it.prontoimmo.app",
              });
            } catch (e) {
              console.warn("[push] register token failed", e?.message); // eslint-disable-line no-console
            }
          },
        ));

        handles.push(await PushNotifications.addListener(
          "registrationError",
          (err) => {
            console.warn("[push] registration error", err); // eslint-disable-line no-console
          },
        ));

        // 2. Push ricevuta MENTRE l'app è APERTA in foreground.
        //    iOS di default non mostra niente; noi la mostriamo come
        //    toast/banner usando sonner (lazy-loaded per non gonfiare).
        handles.push(await PushNotifications.addListener(
          "pushNotificationReceived",
          async (notif) => {
            try {
              const { toast } = await import("sonner");
              const title = notif?.title || notif?.data?.title || "Pronto";
              const body = notif?.body || notif?.data?.body || "";
              const url = notif?.data?.url;
              toast(title, {
                description: body,
                duration: 6000,
                action: url ? {
                  label: "Apri",
                  onClick: () => { try { nav(url); } catch (_) {} },
                } : undefined,
              });
            } catch (_) { /* */ }
          },
        ));

        // 3. Push TAPPATA dal lock screen / notification center.
        //    L'app si apre → navighiamo direttamente alla schermata
        //    pertinente (lead, contatto, agenda...). Senza questo, ogni
        //    push apre solo la dashboard generica.
        handles.push(await PushNotifications.addListener(
          "pushNotificationActionPerformed",
          (action) => {
            try {
              const url = action?.notification?.data?.url;
              if (url && typeof url === "string" && url.startsWith("/")) {
                nav(url);
              }
            } catch (_) { /* */ }
          },
        ));

        await PushNotifications.register();
      } catch (e) {
        console.warn("[push] bootstrap failed", e?.message); // eslint-disable-line no-console
      }
    })();
    return () => {
      handles.forEach((h) => { try { h?.remove?.(); } catch (_) {} });
    };
  }, [user?.user_id, nav]);

  return null;
}
